import { useDispatch, useSelector } from 'react-redux';
import { Link } from "react-router-dom";

import { getPropertyLinks, getQuickLinks, getQuickLinksV2, getMapLinks, getDownloadLinks } from 'utils/propertyLinks'
import Spinner from 'components/Spinner';
import AcrisLink from 'components/externalLinks/AcrisLink';

import { openModal } from 'reducers/modalReducer';
import { downloadFdnyProfilePdf } from 'reducers/fileReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import {
	SOURCE_BIS,
	SOURCE_DOBNOW,
} from 'data/repositories/certificatesOfOccupancyRepository';

// Quick Links entries whose value is a label rather than an address: clicking
// them opens the Certificates of Occupancy popup on the named source, which
// reads the listings we already hold instead of sending the reader to DOB.
const CERTIFICATE_POPUP_SOURCES = {
	'Certificates of Occupancy (BIS)': SOURCE_BIS,
	'Certificates of Occupancy (DOB NOW)': SOURCE_DOBNOW,
};

function ExternalLinksList(props) {
	const dispatch = useDispatch();
	const {
		isLoading,
		propertyLinks,
		propertyData,
		quickLinks,
		mapLinks,
		downloadLinks,
		quickLinksV2,
	} = useSelector(state => ({
		isLoading: state.property.isLoading,
		propertyLinks: getPropertyLinks(state.property.propertyData),
		propertyData: state.property.propertyData,
		quickLinks: getQuickLinks(state.property.propertyData),
		mapLinks: getMapLinks(state.property.propertyData, state.zoning.data),
		downloadLinks: getDownloadLinks(state.property.propertyData),
		quickLinksV2: getQuickLinksV2(state.property.propertyData),
	}));

	let links = propertyLinks;

	if (props.listName === 'quick') {
		links = quickLinks;
	} else if (props.listName === 'maps') {
		links = mapLinks;
	} else if (props.listName === 'downloads') {
		links = downloadLinks;
	} else if (props.listName === 'quickLinksV2') {
		links = quickLinksV2;
	} else {
		links = Object.assign({}, quickLinks, mapLinks, downloadLinks);
	}

	const openFdnyReportModal = () => {
		if (propertyData) {
			return dispatch(openModal(MODAL_TYPES.FDNY_REPORT));
		}
	}

	const downloadFdnyBuildingPdf = () => {
		if (propertyData) {
			return dispatch(downloadFdnyProfilePdf(propertyData.id));
		}
	}

	const openIframeModal = (url, title) => {
		dispatch(openModal(MODAL_TYPES.IFRAME, { url, title }))
	};

	const openCertificatesModal = (source) => {
		dispatch(openModal(MODAL_TYPES.CERTIFICATES_OF_OCCUPANCY, { source }));
	};

	const openHpdICardsModal = () => {
		dispatch(openModal(MODAL_TYPES.HPD_I_CARDS));
	};

	const handleLinkClick = () => {
		if (props.onLinkClick) {
			props.onLinkClick();
		}
	}

	return (
		<div className="quick-links-items-container">
			{isLoading && <Spinner />}
			{Object.keys(links).map((linkText) => {
				const linkUrl = links[linkText];
				if (CERTIFICATE_POPUP_SOURCES[linkText]) {
					return (
						<div key={linkText} className="quick-link-row">
							<button
								className="quick-link-item-btn"
								disabled={!propertyData}
								onClick={() => { openCertificatesModal(CERTIFICATE_POPUP_SOURCES[linkText]); handleLinkClick(); }}
							>
								<span className="quick-link-title">{linkText}</span>
								{!props.noIcons && <i className="bi bi-box-arrow-up-right quick-link-arrow-icon" />}
							</button>
						</div>
					);
				} else if (linkText === 'Acris') {
					return (
						<div key={linkText} className="quick-link-row">
							<AcrisLink buttonClass="quick-link-item-btn">
								<span className="quick-link-title">Acris (Deeds, Zoning Declarations & Exhibits)</span>
								{!props.noIcons && <i className="bi bi-box-arrow-up-right quick-link-arrow-icon" />}
							</AcrisLink>
						</div>
					);
				} else if (linkText === 'Certificate of Occupancy') {
					return (
						<div key={linkText} className="quick-link-row">
							<button
								className="quick-link-item-btn"
								onClick={() => { openIframeModal(linkUrl, 'Certificate of Occupancy'); handleLinkClick(); }}
							>
								<span className="quick-link-title">{linkText}</span>
								{!props.noIcons && <i className="bi bi-box-arrow-up-right quick-link-arrow-icon" />}
							</button>
						</div>
					);
				} else if (linkText === 'FDNY Report') {
					return (
						<div key={linkText} className="quick-link-row">
							<button
								className="quick-link-item-btn"
								disabled={!propertyData}
								onClick={() => { openFdnyReportModal(); handleLinkClick(); }}
							>
								<span className="quick-link-title">{linkText}</span>
								{!props.noIcons && <i className="bi bi-box-arrow-up-right quick-link-arrow-icon" />}
							</button>
						</div>
					);
				} else if (linkText === 'FDNY Building PDF') {
					return (
						<div key={linkText} className="quick-link-row">
							<button
								className="quick-link-item-btn"
								disabled={!propertyData}
								onClick={() => { downloadFdnyBuildingPdf(); handleLinkClick(); }}
							>
								<span className="quick-link-title">{linkText}</span>
								{!props.noIcons && <i className="bi bi-download quick-link-arrow-icon" />}
							</button>
						</div>
					);
				} else if (linkText === 'I-Card (HPD)') {
					return (
						<div key={linkText} className="quick-link-row">
							<button
								className="quick-link-item-btn"
								disabled={!propertyData}
								onClick={() => { openHpdICardsModal(); handleLinkClick(); }}
							>
								<span className="quick-link-title">{linkText}</span>
								{!props.noIcons && <i className="bi bi-images quick-link-arrow-icon" />}
							</button>
						</div>
					);
				} else if (linkText === 'LPC Designation') {
					return (
						<div key={linkText} className="quick-link-row">
							<button
								className="quick-link-item-btn"
								onClick={() => { openIframeModal(linkUrl, 'LPC Designation'); handleLinkClick(); }}
							>
								<span className="quick-link-title">{linkText}</span>
								{!props.noIcons && <i className="bi bi-box-arrow-up-right quick-link-arrow-icon" />}
							</button>
						</div>
					);
				} else if (!links[linkText]) {
					return (
						<div key={linkText} className="quick-link-row">
							<div className="quick-link-item-btn disabled">
								<span className="quick-link-title text-muted">{linkText} (Not available)</span>
							</div>
						</div>
					);
				} else if (typeof links[linkText] === 'string' && links[linkText].charAt(0) === '/') { //internal link
					return (
						<div key={linkText} className="quick-link-row">
							<Link to={links[linkText]} className="quick-link-item-btn" onClick={handleLinkClick}>
								<span className="quick-link-title">{linkText}</span>
								{!props.noIcons && <i className="bi bi-box-arrow-up-right quick-link-arrow-icon" />}
							</Link>
						</div>
					);
				} else {
					return (
						<div key={linkText} className="quick-link-row">
							<a
								href={links[linkText]}
								target="_blank"
								rel="noreferrer"
								className="quick-link-item-btn"
								onClick={handleLinkClick}
							>
								<span className="quick-link-title">{linkText}</span>
								{!props.noIcons && <i className="bi bi-box-arrow-up-right quick-link-arrow-icon" />}
							</a>
						</div>
					);
				}
			})}
		</div>
	);
}

export default ExternalLinksList;
